import React from "react";
import { Loader2, Star, Wallet, ScrollText } from "lucide-react";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { usePointsTerms } from "@/hooks/usePoints";

/**
 * Iter-334 — /rewards-terms. Everything renders from GET /points/terms
 * (single source of truth) — NO numbers are hardcoded here. Mirrors the
 * app's identical /rewards-terms screen.
 */
export default function RewardsTermsPage() {
  const q = usePointsTerms();
  const t = q.data || null;
  const rates = t?.rates || {};

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo
        title="How points work · Allsale Rewards"
        description="Earning rates, redemption rules and the full terms for Allsale Rewards points."
      />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Rewards", href: "/rewards" }, { label: "Points terms" }]} />

      <h1 className="mt-6 font-display text-3xl md:text-4xl font-black text-slate-900" data-testid="rewards-terms-title">
        How points work
      </h1>

      {q.isLoading && (
        <div className="mt-10 grid place-items-center py-16 text-slate-400" data-testid="rewards-terms-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {q.isError && (
        <div className="mt-10 rounded-2xl bg-white shadow-card p-8 text-center" data-testid="rewards-terms-error">
          <p className="text-sm text-slate-500">Couldn&apos;t load the points terms — please try again shortly.</p>
          <button
            type="button"
            onClick={() => q.refetch()}
            data-testid="rewards-terms-retry"
            className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-6 py-2.5 transition-colors"
          >
            Try again
          </button>
        </div>
      )}

      {t && (
        <>
          {/* Rates hero */}
          <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="rewards-terms-rates">
            <RateCard label="Earning · Bazaar orders" value={`${rates.earn_per_nzd} pt / $1`} />
            <RateCard label="Redeeming" value={`${rates.redeem_pts_per_nzd} pts = $1`} />
            <RateCard label="Max per bill" value={`${rates.max_redeem_pct}% of the bill`} />
          </div>

          {/* Earn table */}
          <section className="mt-8">
            <h2 className="flex items-center gap-2 text-base md:text-lg font-bold text-slate-900">
              <Star className="w-4 h-4 text-amber-500" /> Ways to earn
            </h2>
            <ul className="mt-3 rounded-2xl bg-white shadow-card divide-y divide-slate-100" data-testid="rewards-terms-earn">
              {(t.earn || []).map((row, i) => (
                <li key={i} className="flex items-start justify-between gap-4 px-4 py-3 text-sm">
                  <span className="text-slate-700">{row.title}</span>
                  <span className="shrink-0 font-bold text-emerald-700 text-right">{row.points}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Use bullets */}
          <section className="mt-8">
            <h2 className="flex items-center gap-2 text-base md:text-lg font-bold text-slate-900">
              <Wallet className="w-4 h-4 text-slate-500" /> Using your points
            </h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-600" data-testid="rewards-terms-use">
              {(t.use || []).map((line, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" />
                  {line}
                </li>
              ))}
            </ul>
          </section>

          {/* Numbered terms */}
          <section className="mt-8">
            <h2 className="flex items-center gap-2 text-base md:text-lg font-bold text-slate-900">
              <ScrollText className="w-4 h-4 text-slate-500" /> Terms &amp; conditions
            </h2>
            <ol className="mt-3 space-y-2 text-sm text-slate-600 list-decimal pl-5" data-testid="rewards-terms-clauses">
              {(t.terms || []).map((line, i) => (
                <li key={i} className="pl-1">{line}</li>
              ))}
            </ol>
          </section>

          {t.version && (
            <p className="mt-8 text-xs text-slate-400" data-testid="rewards-terms-version">
              Terms version {t.version}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function RateCard({ label, value }) {
  return (
    <div className="rounded-2xl bg-white shadow-card px-4 py-3">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
      <div className="mt-0.5 font-display text-xl font-black text-slate-900">{value}</div>
    </div>
  );
}
